import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useSnackbar } from '@/features/snackbar';

import { getQueryKey as getCompanyNotesQueryKey } from '../useCompanyNotes';
import { getQueryKey as getUserNotesQueryKey } from '../useUserNotes';
import { getQueryKey as getNoteQueryKey } from '../useUserNotes';
import type { CompanyNotesQueryClientData } from '../useCompanyNotes';
import { queryFn } from './queryFn';

type Options = {
  onSuccess?: () => void;
};

export const useUpdateCompanyNote = (options: Options = {}) => {
  const queryClient = useQueryClient();
  const { addSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: queryFn,
    retry: 3,
    onSuccess: ({ id, content, noteCompany }) => {
      addSnackbar({
        type: 'positive',
        lifeTime: 1000,
        message: 'Note successfully updated',
      });
      queryClient.setQueryData<CompanyNotesQueryClientData>(
        getCompanyNotesQueryKey(noteCompany.companyId),
        (state) => {
          if (typeof state === 'undefined') return state;
          return state.map((n) => {
            if (n.id !== id) return n;
            return {
              ...n,
              content,
            };
          });
        }
      );
      options.onSuccess?.();
    },
    onError: (_: Error, variables) => {
      queryClient.cancelQueries({
        queryKey: getCompanyNotesQueryKey(variables.companyId),
      });
      queryClient.cancelQueries({
        queryKey: getNoteQueryKey(variables.id),
      });
    },
    onSettled: (data, __, { companyId }) => {
      queryClient.invalidateQueries({
        queryKey: getCompanyNotesQueryKey(companyId),
      });
      if (data) {
        queryClient.invalidateQueries({
          queryKey: getUserNotesQueryKey(data.userId),
        });
        queryClient.invalidateQueries({ queryKey: getNoteQueryKey(data.id) });
      }
    },
  });
};
